import type { ReactNode } from 'react';
import { BRAND_ICON_SRC, BRAND_LOGO } from '../brand';
import { useStore, type View } from '../state/store';
import { HUB_NAV, type HubView } from './appNav';

/**
 * Home / Retro / Games / Platform / My 用の外枠。
 * Player（エミュレーター）には被せない。既存画面の中身は触らず、ナビだけ足す。
 */
export function AppShell({ children, active }: { children: ReactNode; active: HubView }) {
  const setView = useStore((s) => s.setView);
  const conflictCount = useStore((s) => s.conflictCount);
  const docScroll = active === 'home';

  const go = (v: View) => setView(v);

  return (
    <div
      className={`flex flex-col bg-base-900 ${
        docScroll
          ? 'h-full md:h-auto md:min-h-[var(--app-height)] landscape:h-auto landscape:min-h-[var(--app-height)]'
          : 'h-full'
      }`}
    >
      {/* PC・横持ち: 上部ナビ（Home の文書スクロール時は追従） */}
      <header
        className={`pt-safe-header hidden shrink-0 border-b border-base-600/60 bg-[#07090f] px-4 pb-3 md:block landscape:block ${
          docScroll ? 'sticky top-0 z-20' : ''
        }`}
      >
        <div className="mx-auto flex max-w-6xl items-center gap-4">
          <button
            type="button"
            className="flex shrink-0 items-center gap-2 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
            onClick={() => go('home')}
            aria-label={`${BRAND_LOGO} Home`}
          >
            <img src={BRAND_ICON_SRC} alt="" width={28} height={28} className="h-7 w-7 rounded-lg" draggable={false} />
            <span className="brand-mark text-lg font-semibold tracking-tight text-white">
              EmYu<span className="text-accent">-Ba</span>
            </span>
          </button>

          <nav className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto" aria-label="メイン">
            {HUB_NAV.map((item) => {
              const on = active === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => go(item.id)}
                  className={`btn !min-h-0 shrink-0 !rounded-lg !px-3 !py-1.5 !text-xs ${
                    on
                      ? 'bg-accent/15 text-accent'
                      : 'border-transparent bg-transparent text-slate-400 hover:bg-base-700 hover:text-slate-200'
                  }`}
                  aria-current={on ? 'page' : undefined}
                >
                  {item.label}
                  {item.id === 'my' && conflictCount > 0 && (
                    <span className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[0.6rem] font-bold text-white">
                      {conflictCount}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      <div
        className={`min-h-0 flex-1 ${
          docScroll ? 'overflow-hidden md:overflow-visible landscape:overflow-visible' : 'overflow-hidden'
        }`}
      >
        {children}
      </div>

      {/* スマホ縦: 下部ナビ（横持ち・PCでは出さない＝ゲーム領域を圧迫しない） */}
      <nav
        className="shrink-0 border-t border-base-600/60 bg-[#07090f]/80 backdrop-blur-md md:hidden landscape:hidden"
        style={{ paddingBottom: 'max(0.35rem, env(safe-area-inset-bottom, 0px))' }}
        aria-label="ボトムナビ"
      >
        <div className="grid grid-cols-4 gap-0.5 px-1 pt-1">
          {HUB_NAV.filter((i) => i.bottom).map((item) => {
            const on = active === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => go(item.id)}
                className={`relative flex min-h-12 flex-col items-center justify-center gap-0.5 rounded-xl text-[0.65rem] font-medium touch-manipulation ${
                  on ? 'text-accent' : 'text-slate-500'
                }`}
                aria-current={on ? 'page' : undefined}
              >
                <NavGlyph id={item.id} active={on} />
                <span>{item.short}</span>
                {item.id === 'my' && conflictCount > 0 && (
                  <span className="absolute right-[18%] top-1 h-1.5 w-1.5 rounded-full bg-red-500" aria-hidden />
                )}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

function NavGlyph({ id, active }: { id: HubView; active: boolean }) {
  const stroke = active ? 'currentColor' : 'currentColor';
  const common = { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', 'aria-hidden': true as const };
  switch (id) {
    case 'home':
      return (
        <svg {...common}>
          <path
            d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1v-9.5Z"
            stroke={stroke}
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'library':
      return (
        <svg {...common}>
          <rect x="3" y="5" width="7" height="14" rx="1.2" stroke={stroke} strokeWidth="1.6" />
          <rect x="14" y="5" width="7" height="14" rx="1.2" stroke={stroke} strokeWidth="1.6" />
        </svg>
      );
    case 'games':
      return (
        <svg {...common}>
          <rect x="2" y="7" width="20" height="11" rx="3" stroke={stroke} strokeWidth="1.6" />
          <circle cx="9" cy="12.5" r="1.2" fill={stroke} />
          <circle cx="15" cy="11.5" r="1" fill={stroke} />
          <circle cx="17" cy="13.5" r="1" fill={stroke} />
        </svg>
      );
    case 'my':
      return (
        <svg {...common}>
          <circle cx="12" cy="9" r="3.2" stroke={stroke} strokeWidth="1.6" />
          <path
            d="M5 19.5c1.6-3 4-4.5 7-4.5s5.4 1.5 7 4.5"
            stroke={stroke}
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      );
    default:
      return null;
  }
}
